import { ENABLE_PERMISSION_CHECK } from '../../config/featureFlags';
import { isJuniorCounselRole, roleNameToRoleCode } from '../../rbac/roleService';
import type { RoleCode } from '../../rbac/types';

function isReviewTaskNotReceivedViaForward(taskType?: string, isReceivedViaForward?: boolean): boolean {
  const s = (taskType ?? '').trim().toLowerCase();
  return s === 'review' && isReceivedViaForward !== true;
}

/** Roles that may use Forward/Revert and counsel sub-task create without ASSIGN_SUB_TASKS. */
export const WORKFLOW_ACTION_ELIGIBLE_ROLE_CODES: ReadonlySet<RoleCode> = new Set([
  'SUPERVISING_OFFICER',
  'STATE_ATTORNEY',
]);

/** Allocation Officer identity for sub-task / workflow access rules. */
export function isAllocationOfficerUser(
  roleCode: RoleCode | undefined,
  hasPermission: ((code: string) => boolean) | undefined,
  roleName?: string,
): boolean {
  if (roleNameToRoleCode(roleName ?? '') === 'ALLOCATION_OFFICER') return true;
  if (!ENABLE_PERMISSION_CHECK) return false;
  if (!roleCode || !hasPermission) return false;
  if (roleCode === 'ALLOCATION_OFFICER') return true;
  return hasPermission('ALLOCATION_OFFICER_VIEW');
}

/** Counsel create/list view — not shown to Junior Counsel or Allocation Officer. */
export function canUseCounselSubTasksView(
  roleName: string,
  hasAssignSubTasks: boolean,
  roleCode?: RoleCode,
  hasPermission?: (code: string) => boolean,
): boolean {
  if (isAllocationOfficerUser(roleCode, hasPermission, roleName)) return false;
  if (isJuniorCounselRole(roleName)) return false;
  // Management Assistant is always a sub-task recipient, never a creator — ASSIGN_SUB_TASKS
  // is unrelated to this decision (it only affects Sub Tasks tab editability elsewhere).
  if (roleCode === 'OFFICE_CLERK') return false;
  if (roleCode && WORKFLOW_ACTION_ELIGIBLE_ROLE_CODES.has(roleCode)) return true;
  return hasAssignSubTasks;
}

/** Create sub-task screen vs view sub-task screen — REVIEW tasks not received via forward use the view screen. */
export function shouldUseCounselSubTasksCreateView(
  roleName: string,
  hasAssignSubTasks: boolean,
  taskType?: string,
  isReceivedViaForward?: boolean,
  roleCode?: RoleCode,
  hasPermission?: (code: string) => boolean,
): boolean {
  if (isAllocationOfficerUser(roleCode, hasPermission, roleName)) return false;
  if (isReviewTaskNotReceivedViaForward(taskType, isReceivedViaForward)) return false;
  return canUseCounselSubTasksView(roleName, hasAssignSubTasks, roleCode, hasPermission);
}

/**
 * Sub Tasks tab read-only state — users who may create sub tasks (e.g. Management Assistant
 * with ASSIGN_SUB_TASKS) stay editable even when the rest of the page is in view mode.
 */
export function resolveSubTasksTabReadOnly(input: {
  tabsReadOnly: boolean;
  taskStatusReadOnly: boolean;
  roleName: string;
  hasAssignSubTasks: boolean;
  taskType?: string;
  isReceivedViaForward?: boolean;
  roleCode?: RoleCode;
  hasPermission?: (code: string) => boolean;
}): boolean {
  const canCreate = shouldUseCounselSubTasksCreateView(
    input.roleName,
    input.hasAssignSubTasks,
    input.taskType,
    input.isReceivedViaForward,
    input.roleCode,
    input.hasPermission,
  );
  if (canCreate) return input.taskStatusReadOnly;
  return input.tabsReadOnly;
}

/** Parent case workflow actions — hidden for Allocation Officer and Junior Counsel. */
export function canUseCaseWorkflowActions(
  roleName: string,
  hasAssignSubTasks: boolean,
  roleCode?: RoleCode,
  hasPermission?: (code: string) => boolean,
): boolean {
  if (isAllocationOfficerUser(roleCode, hasPermission, roleName)) return false;
  if (roleCode && WORKFLOW_ACTION_ELIGIBLE_ROLE_CODES.has(roleCode)) return true;
  return canUseCounselSubTasksView(roleName, hasAssignSubTasks, roleCode, hasPermission);
}
